import type React from 'react';
import { useLocation, useNavigate } from 'react-router';

import { useQueryClient } from '@tanstack/react-query';
import { clsx } from 'clsx';
import { getRole, hasDualRole, Roles, setActiveRole } from 'api/tokenStorage';
import { isAuthSessionActive, useAuthSession } from 'hooks/useAuthSession';
import { useUserRoles } from 'hooks/useUserRoles';

import styles from './RoleSwitcher.module.scss';

type RoleSwitcherVariant = 'header' | 'menu';

interface RoleSwitcherProps {
  variant?: RoleSwitcherVariant;
}

/** Show switcher only when the signed-in user has both tutor and student roles. */
const useCanSwitchRoles = (): boolean => {
  const { data: session } = useAuthSession();
  const { data: roles } = useUserRoles(isAuthSessionActive(session));

  const sessionRoles = session?.roles ?? [];
  if (sessionRoles.length > 0) {
    return hasDualRole(sessionRoles);
  }

  if (!roles?.length) {
    return false;
  }

  const hasTutor = roles.some((role) => role.id === Roles.tutor);
  const hasStudent = roles.some((role) => role.id === Roles.student);
  return hasTutor && hasStudent;
};

export const RoleSwitcher: React.FC<RoleSwitcherProps> = ({ variant = 'header' }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const canSwitch = useCanSwitchRoles();

  if (!canSwitch) {
    return null;
  }

  const activeRole = getRole();
  const isTutor = activeRole === 'tutor';

  const handleSwitch = (target: 'tutor' | 'student') => {
    const targetRoleId = target === 'tutor' ? Roles.tutor : Roles.student;
    if ((target === 'tutor' && isTutor) || (target === 'student' && !isTutor)) {
      return;
    }

    setActiveRole(targetRoleId);
    // Keep auth session cache; only drop cabinet data that differs by role.
    void queryClient.invalidateQueries({
      predicate: (query) => query.queryKey[0] !== 'auth',
    });

    if (target === 'tutor') {
      navigate(
        location.pathname.startsWith('/profile/chats') ? '/teacher/chat' : '/teacher',
        { replace: true },
      );
      return;
    }

    navigate(
      location.pathname.startsWith('/teacher/chat') ? '/profile/chats' : '/profile/tasks',
      { replace: true },
    );
  };

  const switcherClass = clsx(
    styles.switcher,
    variant === 'menu' && styles['switcher--menu'],
  );

  const studentClass = clsx(
    styles.option,
    !isTutor && styles.active,
    variant === 'menu' && !isTutor && styles['active--student'],
  );

  const tutorClass = clsx(
    styles.option,
    isTutor && styles.active,
    variant === 'menu' && isTutor && styles['active--tutor'],
  );

  return (
    <div className={switcherClass} role='group' aria-label='Переключение роли'>
      <button
        type='button'
        className={studentClass}
        onClick={() => handleSwitch('student')}
      >
        Ученик
      </button>
      <button
        type='button'
        className={tutorClass}
        onClick={() => handleSwitch('tutor')}
      >
        Репетитор
      </button>
    </div>
  );
};
